/**
 * Atelier Royal: before-after.js (ES module) for <ar-before-after> in
 * sections/royal-before-after.liquid (spec §18, CONVENTIONS §21).
 *
 * Slider (WAI-ARIA slider pattern) on [data-ba-slider]; the value is the share of the finished
 * portrait shown (0–100), exposed as --pos on the element:
 * - Arrow Right/Up +5, Arrow Left/Down −5, Page Up/Down ±20, Home 0, End 100.
 * - Pointer: mouse drags from pointerdown; touch/pen drags after a mostly horizontal move
 *   (vertical swipes keep scrolling the page, touch-action: pan-y), a tap jumps there. Grabbing
 *   the handle drags at once (it has touch-action: none) and keeps the grab point under the finger.
 * - The position is fractional while dragging (smooth on narrow phones); the ARIA value is rounded.
 * - aria-valuetext comes from the translated template in data-value-template ("[percent]").
 * Thumbnails ([data-ba-option], radio group): arrow keys move and select, Home/End; the chosen
 * pair is shown and the create button carries its subject (and style).
 * Theme editor: selecting an example-pair block shows it.
 */

const clamp = (value) => Math.min(100, Math.max(0, value));

class ARBeforeAfter extends HTMLElement {
  connectedCallback() {
    if (this.dataset.arReady) return;
    this.handle = this.querySelector('[data-ba-slider]');
    this.area = this.querySelector('[data-ba-area]');
    if (!this.handle || !this.area) return;
    this.dataset.arReady = 'true';

    this.template = this.handle.dataset.valueTemplate || '';
    this.value = clamp(parseFloat(this.dataset.start) || 50);
    this.options = [...this.querySelectorAll('[data-ba-option]')];
    this.cta = this.querySelector('[data-ba-cta]');
    this.pointer = null;

    this.onKeydown = (event) => {
      const steps = { ArrowRight: 5, ArrowUp: 5, ArrowLeft: -5, ArrowDown: -5, PageUp: 20, PageDown: -20 };
      if (event.key in steps) this.set(Math.round(this.value) + steps[event.key]);
      else if (event.key === 'Home') this.set(0);
      else if (event.key === 'End') this.set(100);
      else return;
      event.preventDefault();
    };

    this.onPointerDown = (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      const onHandle = Boolean(event.target.closest?.('[data-ba-slider]'));
      this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, dragging: false, offset: 0 };
      if (onHandle) {
        const rect = this.handle.getBoundingClientRect();
        this.pointer.offset = event.clientX - (rect.left + rect.width / 2);
      }
      if (event.pointerType === 'mouse' || onHandle) {
        event.preventDefault();
        if (onHandle) this.handle.focus({ preventScroll: true }); // the press no longer focuses it by itself; arrow keys keep working
        this.startDrag(event);
      }
    };
    this.onPointerMove = (event) => {
      const p = this.pointer;
      if (!p || p.id !== event.pointerId) return;
      if (!p.dragging) {
        const dx = Math.abs(event.clientX - p.x);
        const dy = Math.abs(event.clientY - p.y);
        if (dx < 6 || dx < dy) return;
        this.startDrag(event);
      }
      this.pendingX = event.clientX - p.offset;
      if (!this.frame) {
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.moveTo(this.pendingX);
        });
      }
    };
    this.onPointerUp = (event) => {
      const p = this.pointer;
      if (!p || p.id !== event.pointerId) return;
      if (!p.dragging && event.type === 'pointerup') this.moveTo(event.clientX); // tap
      this.pointer = null;
      this.classList.remove('is-dragging');
    };
    /* Only the area's own capture ending stops a drag. A touch on the handle is implicitly captured
       by the handle; moving that capture to the area fires lostpointercapture on the handle, which
       bubbles here and used to end the drag after one step. */
    this.onLostCapture = (event) => {
      if (event.target === this.area) this.onPointerUp(event);
    };

    this.onOptionClick = (event) => {
      const option = event.target.closest('[data-ba-option]');
      if (option) this.choose(this.options.indexOf(option));
    };
    this.onOptionKeydown = (event) => {
      const index = this.options.indexOf(event.target.closest('[data-ba-option]'));
      if (index < 0) return;
      const last = this.options.length - 1;
      let next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = index === last ? 0 : index + 1;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = index === 0 ? last : index - 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = last;
      if (next === null) return;
      event.preventDefault();
      this.choose(next, true);
    };
    this.onIntent = (event) => {
      const option = event.target.closest?.('[data-ba-option]');
      if (option) this.warm(this.pairFor(option));
    };
    this.onBlockSelect = (event) => {
      const pair = event.target.closest?.('[data-pair]');
      if (!pair) return;
      const index = this.options.findIndex((o) => o.getAttribute('aria-controls') === pair.id);
      if (index >= 0) this.choose(index);
    };

    this.handle.addEventListener('keydown', this.onKeydown);
    this.area.addEventListener('pointerdown', this.onPointerDown);
    this.area.addEventListener('pointermove', this.onPointerMove);
    this.area.addEventListener('pointerup', this.onPointerUp);
    this.area.addEventListener('pointercancel', this.onPointerUp);
    this.area.addEventListener('lostpointercapture', this.onLostCapture);
    const group = this.options[0]?.closest('[role="radiogroup"]');
    if (group) {
      group.addEventListener('click', this.onOptionClick);
      group.addEventListener('keydown', this.onOptionKeydown);
      group.addEventListener('pointerover', this.onIntent, { passive: true });
      group.addEventListener('touchstart', this.onIntent, { passive: true });
      group.addEventListener('focusin', this.onIntent);
    }
    this.addEventListener('shopify:block:select', this.onBlockSelect);

    this.set(this.value);
  }

  disconnectedCallback() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  startDrag(event) {
    this.pointer.dragging = true;
    this.area.setPointerCapture?.(event.pointerId);
    this.classList.add('is-dragging');
    this.moveTo(event.clientX - this.pointer.offset);
  }

  moveTo(clientX) {
    const rect = this.area.getBoundingClientRect();
    if (!rect.width) return;
    this.set(((clientX - rect.left) / rect.width) * 100);
  }

  set(value) {
    this.value = clamp(value);
    this.style.setProperty('--pos', `${this.value.toFixed(2)}%`);
    const rounded = Math.round(this.value);
    if (rounded === this.announced) return;
    this.announced = rounded;
    this.handle.setAttribute('aria-valuenow', String(rounded));
    if (this.template) this.handle.setAttribute('aria-valuetext', this.template.replace('[percent]', String(rounded)));
  }

  pairFor(option) {
    const id = option?.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  warm(pair) {
    pair?.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  }

  choose(index, focus = false) {
    const option = this.options[index];
    if (!option) return;
    this.options.forEach((o, i) => {
      o.setAttribute('aria-checked', String(i === index));
      o.tabIndex = i === index ? 0 : -1;
    });
    if (focus) option.focus();

    const next = this.pairFor(option);
    if (next) {
      this.warm(next);
      this.querySelectorAll('[data-pair]').forEach((pair) => {
        pair.hidden = pair !== next;
      });
    }

    const base = this.cta?.dataset.baseHref;
    if (base) {
      const params = new URLSearchParams();
      if (option.dataset.subject) params.set('subject', option.dataset.subject);
      if (option.dataset.style) params.set('style', option.dataset.style);
      const query = params.toString();
      this.cta.href = query ? `${base}?${query}` : base;
    }
  }
}

if (!customElements.get('ar-before-after')) customElements.define('ar-before-after', ARBeforeAfter);
